import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { ensureBotConfigForRoute } from '../../config/getBotConfig';
import { setCurrentConfig } from '../../config/currentBotConfig';
import { BotConfigProvider } from '../../context/BotConfigContext';
import Chat from '../chat/Chat';
import applyTheme from '../../theme/applyTheme';
import useLayoutService from '../../services/layout/layoutService';
import AdminSettingsPanel from './AdminSettingsPanel';
import {
  buildAdminPayload,
  buildExportPayload,
  migrateStoredDraft,
} from './adminDraftPayload';
import {
  clearAdminDraft,
  loadAdminDraft,
  saveAdminDraft,
} from './adminDraftStorage';
import { validateDiscoveryButtons } from './discoveryButtonsValidation';
import { downloadAdminExportZip } from './adminConfigExport';
import {
  clearLocalBotIcon,
  loadLocalBotIcon,
} from './botIconLocalStorage';
import classes from './AdminDesignerPage.module.css';
import appClasses from '../App.module.css';

function createBaseDraft(cfg, domainName, botName) {
  const path = `/admin/${domainName}/${botName}`;
  return {
    ...JSON.parse(JSON.stringify(cfg)),
    botUrl: window.location.origin + path,
    isDesignPreview: true,
    discoveryButton: Array.isArray(cfg?.discoveryButton)
      ? JSON.parse(JSON.stringify(cfg.discoveryButton))
      : [],
  };
}

function downloadJson(filename, dataObj) {
  const blob = new Blob([JSON.stringify(dataObj, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function AdminDesignerPage() {
  const { domainName, botName } = useParams();
  useLayoutService();

  const [draft, setDraft] = useState(null);
  const [loadState, setLoadState] = useState('loading');
  const [draftRestoredNotice, setDraftRestoredNotice] = useState(false);
  const [exportError, setExportError] = useState('');
  const [exportingZip, setExportingZip] = useState(false);
  const [originalBotIconUrl, setOriginalBotIconUrl] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoadState('loading');
    setDraft(null);
    setDraftRestoredNotice(false);
    setExportError('');

    ensureBotConfigForRoute(domainName, botName)
      .then((cfg) => {
        if (cancelled) return;
        if (!cfg) {
          setLoadState('error');
          return;
        }

        const baseDraft = createBaseDraft(cfg, domainName, botName);
        setOriginalBotIconUrl(baseDraft.botIconUrl ?? '');

        const stored = loadAdminDraft(domainName, botName);
        let { draft: nextDraft, migrated } = migrateStoredDraft(stored, baseDraft);

        if (migrated) {
          setDraftRestoredNotice(true);
        }

        const localIcon = loadLocalBotIcon(domainName, botName);
        if (localIcon?.url) {
          nextDraft = { ...nextDraft, botIconUrl: localIcon.url };
        }

        setDraft(nextDraft);
        setLoadState('ready');
      })
      .catch(() => {
        if (!cancelled) setLoadState('error');
      });

    return () => {
      cancelled = true;
    };
  }, [domainName, botName]);

  useEffect(() => {
    if (loadState !== 'ready' || !draft) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      const payload = buildAdminPayload(draft);
      saveAdminDraft(domainName, botName, { payload });
    }, 400);

    return () => window.clearTimeout(timer);
  }, [draft, loadState, domainName, botName]);

  const previewRef = useRef(null);
  const [sidebarEl, setSidebarEl] = useState(null);

  useEffect(() => {
    if (draft) setCurrentConfig(draft);
  }, [draft]);

  useEffect(() => {
    const el = previewRef.current;
    if (el && draft?.theme) applyTheme(draft.theme, el);
  }, [draft?.theme, draft]);

  const onDiscoveryButtonsChange = useCallback((buttons) => {
    setExportError('');
    setDraft((d) => ({
      ...d,
      discoveryButton: buttons,
    }));
  }, []);

  const onExportConfig = useCallback(() => {
    if (!draft || !botName) return;

    const discovery = validateDiscoveryButtons(draft.discoveryButton ?? []);
    if (!discovery.ok) {
      setExportError(discovery.error);
      return;
    }

    setExportError('');
    const payload = buildExportPayload(draft, { botName, domainName });
    downloadJson(`${botName}_config.json`, payload);
  }, [draft, botName, domainName]);

  const onExportZip = useCallback(async () => {
    if (!draft || !botName || exportingZip) return;

    const discovery = validateDiscoveryButtons(draft.discoveryButton ?? []);
    if (!discovery.ok) {
      setExportError(discovery.error);
      return;
    }

    setExportError('');
    setExportingZip(true);

    try {
      await downloadAdminExportZip({ botName, domainName, draft });
    } catch {
      setExportError('נכשלה יצירת הקובץ');
    } finally {
      setExportingZip(false);
    }
  }, [draft, botName, domainName, exportingZip]);

  const onResetDraft = useCallback(async () => {
    clearAdminDraft(domainName, botName);
    clearLocalBotIcon(domainName, botName);
    setDraftRestoredNotice(false);
    setExportError('');
    setLoadState('loading');
    setDraft(null);

    try {
      const cfg = await ensureBotConfigForRoute(domainName, botName);
      if (!cfg) {
        setLoadState('error');
        return;
      }

      const baseDraft = createBaseDraft(cfg, domainName, botName);
      setOriginalBotIconUrl(baseDraft.botIconUrl ?? '');
      setDraft(baseDraft);
      setLoadState('ready');
    } catch {
      setLoadState('error');
    }
  }, [domainName, botName]);

  if (loadState === 'loading') {
    return null;
  }

  if (loadState === 'error' || !draft) {
    return <Navigate to="/admin" replace />;
  }

  return (
    <div className={classes.layout}>
      <aside ref={setSidebarEl} className={classes.sidebar}>
        <AdminSettingsPanel
          key={`${domainName}-${botName}`}
          domainName={domainName}
          botName={botName}
          draft={draft}
          setDraft={setDraft}
          discoveryButtons={draft.discoveryButton ?? []}
          onDiscoveryButtonsChange={onDiscoveryButtonsChange}
          originalBotIconUrl={originalBotIconUrl}
          onExportConfig={onExportConfig}
          onExportZip={onExportZip}
          exportingZip={exportingZip}
          onResetDraft={onResetDraft}
          exportError={exportError}
          setExportError={setExportError}
          draftRestoredNotice={draftRestoredNotice}
          modalContainer={sidebarEl}
        />
      </aside>
      <div ref={previewRef} className={classes.preview}>
        <div className={classes.previewInner}>
          <BotConfigProvider value={draft}>
            <div className={appClasses.App}>
              <Chat />
            </div>
          </BotConfigProvider>
        </div>
      </div>
    </div>
  );
}
